import { Form } from "antd";
import {
  BUYER_INQUIRY_LIST_ENDPOINT,
  GENERATE_PLANNING_DETAILS,
  GET_INQUIRY_STYLE_DETAILS,
  GET_LINE_LIST,
  GET_STYLE_LIST_ENDPOINT,
  PLAN_INITIATE,
  REGENERATE_PLAN,
  SUBSTYLE_LIST,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData, putData } from "apiServices/common";
import HandleNotification from "common/Notification";
import moment from "moment";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { commaSeparateNumber, isArrayAndHasValue } from "utils/functions";

export const ProductionPlanningContext = createContext();

const order_status = {
  0: "Inquiry",
  1: "Contracted",
  2: "Closed",
};

/** Quantity without the extra cutting %: (100 × qty) / (100 + extra %). */
export const calculateOriginOrderQty = (qty, extraPercent) => {
  const baseQty = Number(qty) || 0;
  const percent = parseFloat(extraPercent) || 0;
  return Math.round((100 * baseQty) / (100 + percent));
};

const buildPcdRequestOptions = (inquiryDetails) =>
  (inquiryDetails?.plan_requests || []).map((request, index) => {
    const requestQty = Number(request?.request_qty) || 0;
    return {
      value: index,
      pcd: request?.pcd || null,
      request_qty: requestQty,
      label: `${request?.pcd || "No PCD"} (Qty: ${commaSeparateNumber(requestQty)})`,
    };
  });

const ProductionPlanningContextProvider = ({ children }) => {
  // router
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const line_id = searchParams.get("line_id");
  const start_date = searchParams.get("start_date");
  const inquiry_id_param = searchParams.get("inquiry_id") || location.state?.inquiry_id;
  const inquiry_no_param = searchParams.get("inquiry_no") || location.state?.inquiry_no;
  const order_qty_param = searchParams.get("order_qty") || location.state?.order_qty;
  const shipment_date_param = searchParams.get("shipment_date") || location.state?.shipment_date;

  const initialInquiryItem =
    location.state?.buyer_inquiry ||
    (inquiry_id_param
      ? {
          id: parseInt(inquiry_id_param),
          inquiry_no: inquiry_no_param || `Inquiry #${inquiry_id_param}`,
        }
      : null);

  // States
  const [planningDetails, setPlanningDetails] = useState(null);
  const [existingPlan, setExistingPlan] = useState(null);
  const [overlapPlans, setOverlapPlans] = useState(null);
  const [planningOverlapping, setPlanningOverlapping] = useState([{}]);
  const [inquiryStyleDetails, setInquiryStyleDetails] = useState({});
  const [selectedPlanDetails, setSelectedPlanDetails] = useState(null);
  const [options, setOptions] = useState({
    lines: [],
    inquiryList: initialInquiryItem ? [initialInquiryItem] : [],
    styleList: [],
    subStyleList: [],
    po: [],
    pcdRequests: [],
  });
  // When set, "With (%) Qty" uses this value instead of origin qty + cutting %.
  const lockedOrderQtyRef = useRef(null);
  const [createdWith, setCreatedWith] = useState(null);
  const [changeStatus, setChangeStatus] = useState(null);
  const [isGenerateBtnEnable, setIsGenerateBtnEnable] = useState(false);
  // Antd
  const [form] = Form.useForm();
  const inquiry_id_watch = Form.useWatch("inquiry_id", form);
  const style_id_watch = Form.useWatch("style_id", form);
  const sales_contract_po_id_watch = Form.useWatch("sales_contract_po_id", form);

  const order_qty_watch = Form.useWatch("order_qty", form);
  const origin_order_qty_watch = Form.useWatch("origin_order_qty", form);
  const extra_cutting_percentage_watch = Form.useWatch("extra_cutting_percentage", form);
  const planned_qty_watch = Form.useWatch("planned_qty", form);
  const existing_plan_sum_watch = Form.useWatch("existing_plan_sum", form);
  const actual_qty_watch = Form.useWatch("actual_qty", form);

  // get GET_LINE_LIST
  const getLineList = useCallback(async (filterValues) => {
    try {
      const payload = {
        ...filterValues,
      };
      const response = await getData(GET_LINE_LIST, false, payload);
      if (response.status === 200) {
        const lines = response.data?.data?.data?.map((line) => ({
          value: line.id,
          label: line.name,
        }));
        setOptions((prev) => ({ ...prev, lines }));
      }
    } catch (error) {
      HandleNotification("error", "bottomRight", "Error getting line data", null);
    }
  }, []);

  // Get inquiry list data
  const getInquiryList = useCallback(async (filterValues) => {
    try {
      const bodyData = {
        ...filterValues,
        per_page: filterValues?.per_page || 10,
      };
      const response = await getData(BUYER_INQUIRY_LIST_ENDPOINT, false, bodyData);

      if (response && response?.data?.code === 200) {
        let inquiryList = response?.data?.data?.data || [];
        if (inquiry_id_param) {
          const numId = parseInt(inquiry_id_param);
          const exists = inquiryList.some((item) => item?.id === numId);
          if (!exists) {
            const extraItem = location.state?.buyer_inquiry || {
              id: numId,
              inquiry_no: inquiry_no_param || `Inquiry #${numId}`,
            };
            inquiryList = [extraItem, ...inquiryList];
          }
        }
        setOptions((prev) => ({ ...prev, inquiryList }));
      }
    } catch (error) {
      HandleNotification("error", "bottomRight", "Error getting inquiry data", null);
    }
  }, [inquiry_id_param, inquiry_no_param, location.state]);

  // Get style list data
  const getStyleList = useCallback(async (filterValues) => {
    try {
      const bodyData = {
        ...filterValues,
        per_page: filterValues?.per_page || 10,
        active_sales_contract: true,
      };
      const response = await getData(GET_STYLE_LIST_ENDPOINT, false, bodyData);

      if (response && response?.data?.code === 200) {
        const styleList = response?.data?.data?.data;
        setOptions((prev) => ({ ...prev, styleList }));
      }
    } catch (error) {
      HandleNotification("error", "bottomRight", "Error getting style data", null);
    }
  }, []);

  // Get sub style list data
  const getSubStyleList = useCallback(async (filterValues) => {
    try {
      const bodyData = {
        ...filterValues,
        per_page: filterValues?.per_page || 10,
      };
      const response = await getData(SUBSTYLE_LIST, false, bodyData);

      if (response && response?.data?.code === 200) {
        const subStyleList = response?.data?.data?.data;
        setOptions((prev) => ({ ...prev, subStyleList }));
      }
    } catch (error) {
      HandleNotification("error", "bottomRight", "Error getting sub style data", null);
    }
  }, []);

  // Start Date Change to check if it is greater button enable or not
  const startDateOrLineChange = useCallback(async ({ line_id, start_date }) => {
    const res = await getData(`/api/productionPlanning/is_plane_created_this_date`, false, {
      line_id: line_id || null,
      date: start_date ? moment(start_date).format("YYYY-MM-DD") : null,
    });
    if (res && res?.data?.code === 200) {
      const isEnable = res?.data?.data?.[0]?.is_generate_btn_enable;
      setIsGenerateBtnEnable(isEnable);
    } else {
      HandleNotification("error", "bottomRight", "Error checking start date", null);
    }
  }, []);

  // First Time render set the options
  useEffect(() => {
    const initialFields = {
      apply_learning_curve: true,
      line: parseInt(line_id) || null,
      start_date: start_date ? moment(start_date) : null,
      extra_cutting_percentage: 0, // Initialize cutting percentage to 0
    };

    if (inquiry_id_param) {
      const numInquiryId = parseInt(inquiry_id_param);
      initialFields.inquiry_id = numInquiryId;
      if (order_qty_param !== undefined && order_qty_param !== null && order_qty_param !== "") {
        const numQty = Number(order_qty_param);
        initialFields.order_qty = numQty;
        initialFields.origin_order_qty = numQty;
        initialFields.origin_order_qty_display = numQty;
      }
      if (shipment_date_param && moment(shipment_date_param).isValid()) {
        initialFields.shipment_date = moment(shipment_date_param);
      }
    }

    form.setFieldsValue(initialFields);
    getLineList({ per_page: 10000 });
    getInquiryList();
    getStyleList();
    getSubStyleList();
  }, [getLineList, getInquiryList, getStyleList, getSubStyleList, form, line_id, start_date]);

  // Calculate order_qty when origin_order_qty or extra_cutting_percentage changes
  useEffect(() => {
    if (!origin_order_qty_watch) return;

    const originQty = Number(origin_order_qty_watch) || 0;
    const cuttingPercentage = parseFloat(extra_cutting_percentage_watch) || 0;
    const calculatedOrderQty =
      lockedOrderQtyRef.current != null
        ? lockedOrderQtyRef.current
        : Math.ceil(originQty + (originQty * cuttingPercentage) / 100);

    form.setFieldsValue({
      order_qty: calculatedOrderQty,
      origin_order_qty_display: originQty, // For display purposes
    });
  }, [origin_order_qty_watch, extra_cutting_percentage_watch, form]);

  useEffect(() => {
    // Find remaining qty from current planned qty and sum of all existing plans
    if (!order_qty_watch) return;

    // Fixed calculation: actual_qty should be considered as already produced/completed
    // So it should reduce the remaining quantity along with planned quantities
    const remaining_qty =
      order_qty_watch - ((planned_qty_watch || 0) + (existing_plan_sum_watch || 0) + parseInt(actual_qty_watch || 0));

    // Ensure remaining quantity doesn't go below 0
    const finalRemainingQty = Math.max(0, remaining_qty);

    form.setFieldsValue({
      remaining_qty: finalRemainingQty,
    });
  }, [planned_qty_watch, order_qty_watch, existing_plan_sum_watch, actual_qty_watch, form]);

  const getStyleId = (props) => {
    const { sub_style_id_prop, style_id_prop, options } = props;

    if (style_id_prop) {
      return style_id_prop;
    }

    const subStyleList = isArrayAndHasValue(options?.subStyleList) ? [...options?.subStyleList] : [];
    const foundSubStyle = subStyleList?.find((item) => item?.id === sub_style_id_prop);
    const foundStyleId = foundSubStyle?.style_id || null;

    return foundStyleId;
  };

  // on change Inquiry & Style
  const onInquiryAndStyleChange = useCallback(
    async (props, from, editModeData = null) => {
      const { line_id_prop, inquiry_id_prop, style_id_prop, sub_style_id_prop, po_map_ids, options } = props;
      const selectedPoMapIds = isArrayAndHasValue(po_map_ids) ? po_map_ids : [];
      try {
        const line_id = form.getFieldValue("line");
        const sub_style_id = form.getFieldValue("sub_style_id");

        let payload = {
          line_id: line_id || line_id_prop,
          ...(selectedPoMapIds.length ? { po_map_ids: selectedPoMapIds } : {}),
        };

        if (inquiry_id_prop) {
          payload = {
            ...payload,
            inquiry_id: inquiry_id_prop,
          };
        }
        if (style_id_prop || sub_style_id_prop) {
          payload = {
            ...payload,
            style_id: getStyleId({ sub_style_id_prop, style_id_prop, options }),
            sub_style_id: sub_style_id_prop || sub_style_id,
          };
        }

        const API_ENDPOINTS = `${GET_INQUIRY_STYLE_DETAILS}`;
        const response = await getData(API_ENDPOINTS, false, payload);

        if (response.status === 200) {
          const data = response.data?.data;
          const obData = data?.operation_bulletin_info;
          const existing_plan_data = data?.existing_plan;
          const learning_curve_info = data?.learningCurveData?.learning_curve_info;

          setExistingPlan(isArrayAndHasValue(existing_plan_data) ? existing_plan_data : null);

          if (obData?.message) {
            HandleNotification("warning", "bottomRight", obData?.message, null);
          }
          if (!obData) {
            HandleNotification("error", "bottomRight", "OB data not found", null);
          }

          // set po options
          setOptions((prev) => ({
            ...prev,
            po: data?.sales_contract_po?.map((po) => ({
              ...po,
              value: po?.id,
              label: po?.original_po,
            })),
          }));

          if (data?.style_details?.sub_style && isArrayAndHasValue(data?.style_details?.sub_style)) {
            setOptions((prev) => ({
              ...prev,
              subStyleList: data?.style_details?.sub_style,
            }));
          }

          if (data?.inquiry_details) {
            setOptions((prev) => {
              const existingList = prev?.inquiryList || [];
              const exists = existingList.some((item) => item?.id === data.inquiry_details?.id);
              return exists
                ? prev
                : {
                    ...prev,
                    inquiryList: [data.inquiry_details, ...existingList],
                  };
            });
          }

          const dataMake = {
            ...data,
          };

          const isInquiryMode = Boolean(data?.is_inquiry_id);
          const isSubStyleMode = !isInquiryMode && Boolean(sub_style_id_prop);

          const pcdRequests = isInquiryMode ? buildPcdRequestOptions(data?.inquiry_details) : [];
          const savedReqQty = Number(editModeData?.req_qty) || 0;
          const selectedPcdRequest = isArrayAndHasValue(pcdRequests)
            ? (from === "edit_mode" && pcdRequests.find((request) => request.request_qty === savedReqQty)) ||
              pcdRequests[0]
            : null;
          setOptions((prev) => ({ ...prev, pcdRequests }));

          if (from !== "edit_mode") {
            // check inquiry selected or not
            if (data?.inquiry_details && data?.inquiry_details?.id) {
              // If inquiry status is Contracted (1)
              if (data?.inquiry_details?.inquiry_status === 1) {
                // If any PO is selected
                if (isArrayAndHasValue(sales_contract_po_id_watch)) {
                  // In this case, shipment date will come from selected PO
                  dataMake.shipment_date = null;
                } else {
                  dataMake.shipment_date = data?.inquiry_details?.expected_delivery_date
                    ? moment(data?.inquiry_details?.expected_delivery_date)
                    : null;
                }
              }
              // If inquiry status is Inquiry (0)
              if (data?.inquiry_details?.inquiry_status === 0) {
                dataMake.shipment_date = data?.inquiry_details?.expected_delivery_date
                  ? moment(data?.inquiry_details?.expected_delivery_date)
                  : null;
              }
            } else {
              // inquiry not found
              // shipment_date (pcd) set from the earliest delivery date of the selected POs
              if (selectedPoMapIds.length && isArrayAndHasValue(data?.sales_contract_po)) {
                const deliveryDates = data.sales_contract_po
                  .filter((item) => selectedPoMapIds.includes(item?.id) && item?.delivery_date)
                  .map((item) => moment(item.delivery_date));
                dataMake.shipment_date = deliveryDates.length ? moment.min(deliveryDates) : null;
              } else {
                // When inquiry not found
                dataMake.shipment_date = null;
              }
            }

            if (selectedPcdRequest?.pcd) {
              dataMake.shipment_date = moment(selectedPcdRequest.pcd);
            } else if (!isInquiryMode && data?.pcd && moment(data.pcd).isValid()) {
              dataMake.shipment_date = moment(data.pcd);
            }
          }

          if (from === "edit_mode") {
            // For edit mode, no_of_workers will be used from show API
            delete dataMake["no_of_workers"];
          }

          const apiExtraCuttingPercentage = parseFloat(data?.extra_cutting_percentage) || 0;
          const hasExistingCuttingPercentage = apiExtraCuttingPercentage > 0;
          const apiOrderQty = Number(data?.order_qty) || 0;

          const isInquirySelected = Boolean(dataMake?.is_inquiry_id || inquiry_id_prop);
          const inquiryExtraPercent =
            dataMake?.inquiry_details?.extra_percent != null ? parseFloat(dataMake?.inquiry_details?.extra_percent) : null;

          let resolvedCuttingPercentage = 0;
          if (isInquiryMode && inquiryExtraPercent != null) {
            resolvedCuttingPercentage = inquiryExtraPercent;
          } else if (hasExistingCuttingPercentage || isSubStyleMode) {
            resolvedCuttingPercentage = apiExtraCuttingPercentage;
          } else if (isInquirySelected) {
            resolvedCuttingPercentage = inquiryExtraPercent || 0;
          } else {
            resolvedCuttingPercentage = form.getFieldValue("extra_cutting_percentage") || 0;
          }

          // Inquiry: qty comes from the selected PCD request (or the inquiry's expected qty).
          // Style: qty comes from the API. Both include the cutting %, so origin qty is derived from it.
          // Sub style: origin qty, cutting % and "With (%) Qty" all come from the API as-is.
          let originOrderQty;
          let withPercentQty;
          if (isSubStyleMode) {
            originOrderQty = apiOrderQty;
            withPercentQty =
              Number(data?.req_qty) > 0
                ? Number(data.req_qty)
                : Math.ceil(originOrderQty + (originOrderQty * resolvedCuttingPercentage) / 100);
          } else {
            const qtyWithPercent = isInquiryMode
              ? selectedPcdRequest
                ? selectedPcdRequest.request_qty
                : Number(data?.inquiry_details?.expected_order_quantity) || 0
              : apiOrderQty;
            originOrderQty = calculateOriginOrderQty(qtyWithPercent, resolvedCuttingPercentage);
            withPercentQty = qtyWithPercent;
          }
          lockedOrderQtyRef.current = withPercentQty;

          // set form data
          const formData = {
            ...dataMake,
            pcd: isInquiryMode ? selectedPcdRequest?.pcd || null : dataMake?.pcd,
            pcd_request_key: selectedPcdRequest ? selectedPcdRequest.value : undefined,
            origin_order_qty: originOrderQty,
            origin_order_qty_display: originOrderQty,
            extra_cutting_percentage: resolvedCuttingPercentage,
            order_qty: withPercentQty,
            order_status_name: order_status[parseInt(data.order_status)],
            ob_id: obData?.id,
            learning_curve_id: learning_curve_info?.id,
            use_both_style_po: data?.sales_contract?.use_both_style_po,
            inquiry_id: dataMake?.is_inquiry_id ? dataMake?.inquiry_details?.id : null,
            inquiry_id_when_not_selected: !dataMake?.is_inquiry_id ? dataMake?.inquiry_details?.id : null,
          };

          // Override with planning request parameters if provided
          if (inquiry_id_param) {
            formData.inquiry_id = parseInt(inquiry_id_param);
          }
          if (order_qty_param !== undefined && order_qty_param !== null && order_qty_param !== "") {
            const customQty = Number(order_qty_param);
            formData.origin_order_qty = customQty;
            formData.origin_order_qty_display = customQty;
            formData.order_qty = customQty;
            lockedOrderQtyRef.current = customQty;
          }
          if (shipment_date_param && moment(shipment_date_param).isValid()) {
            formData.shipment_date = moment(shipment_date_param);
          }

          // Only set planned_qty to 0 in create mode, not in edit mode
          if (from !== "edit_mode") {
            formData.planned_qty = 0; // Sultan Vai: one plan after submit api call, it will be set to plan_qty base on backend response when select po or on plan Submit success
          }

          form.setFieldsValue(formData);

          // For edit mode, set extra_cutting_percentage and order_qty from editModeData after API returns
          if (from === "edit_mode" && editModeData) {
            const editExtraCuttingPercentage = parseFloat(editModeData?.extra_cutting_percentage) || 0;
            const editReqQty = editModeData?.req_qty || editModeData?.order_qty || 0;
            lockedOrderQtyRef.current = editReqQty;

            form.setFieldsValue({
              extra_cutting_percentage: editExtraCuttingPercentage,
              order_qty: editReqQty,
            });
          }

          // set inquiry style details
          setInquiryStyleDetails((prev) => ({ ...prev, ...dataMake }));
        }
      } catch (error) {
        HandleNotification("error", "bottomRight", "Error getting data", null);
      }
    },
    [form],
  );

  const onPcdRequestChange = (key) => {
    const request = options?.pcdRequests?.find((item) => item.value === key);
    if (!request) return;

    const cuttingPercentage = form.getFieldValue("extra_cutting_percentage");
    const originOrderQty = calculateOriginOrderQty(request.request_qty, cuttingPercentage);
    lockedOrderQtyRef.current = request.request_qty;

    form.setFieldsValue({
      pcd: request.pcd,
      origin_order_qty: originOrderQty,
      origin_order_qty_display: originOrderQty,
      order_qty: request.request_qty,
      planned_qty: 0,
      ...(request.pcd ? { shipment_date: moment(request.pcd) } : {}),
    });
    setPlanningDetails(null);
  };

  const resetPcdRequests = () => {
    lockedOrderQtyRef.current = null;
    setOptions((prev) => ({ ...prev, pcdRequests: [] }));
    form.setFieldsValue({ pcd: null, pcd_request_key: undefined });
  };

  const shipmentDateSetter = (dateString) => {
    if (!dateString) return;

    if (inquiryStyleDetails?.inquiry_details?.id) {
      // If there is inquiry and inquiry status is Contracted (1)
      console.log("if 1");

      if (inquiryStyleDetails?.inquiry_details?.inquiry_status === 1) {
        console.log("if 2");
        form.setFieldsValue({
          shipment_date: moment(dateString),
        });
      }
    } else {
      // If inquiry not found
      // Style is active sales contract
      form.setFieldsValue({
        shipment_date: moment(dateString),
      });
    }
  };

  const onGenerate = useCallback(
    async (values) => {
      const remaining_qty = parseInt(values?.remaining_qty) || 0;
      const planned_qty = parseInt(values?.planned_qty) || 0;
      const totalRemainingQty = remaining_qty;

      const payload = {
        line_id: values.line || null,
        inquiry_id: values?.inquiry_id || values?.inquiry_id_when_not_selected || null,
        style_id: values?.style_id || null,
        ob_id: values.ob_id || null,
        start_date: values.start_date ? moment(values.start_date).format("YYYY-MM-DD") : null,
        sales_contact_id: values.sales_contract.id || null,
        // order_qty: values?.order_qty,
        remaining_qty: totalRemainingQty || 0,
        // req_qty: values?.req_qty || values?.order_qty,
        extra_cutting_percentage: values?.extra_cutting_percentage || 0,
        update_id: values?.update_id || null,
        created_with: createdWith || null,
        po_map_ids: isArrayAndHasValue(values?.sales_contract_po_id) ? values.sales_contract_po_id : [],
        is_apply_learning_curve: values?.is_apply_learning_curve ? 1 : 0,
      };

      const query = `${GENERATE_PLANNING_DETAILS}`;
      const response = await getData(query, false, payload);

      if (response && response?.data?.code === 200) {
        const planningData = response?.data?.data?.planning_details?.days;
        const customPlanningData =
          isArrayAndHasValue(planningData) &&
          planningData?.map((item) => {
            const planQty = item?.dayQty;
            return {
              ...item,
              plan_curve_mh: item?.working_hours,
              plan_curve_efficiency: item?.efficiency,
              plan_curve_qty: planQty,
              base_plan_curve_qty: planQty,
            };
          });

        if (!isArrayAndHasValue(planningData)) {
          HandleNotification("error", "bottomRight", "Planning data not found", null);
          return;
        }

        setPlanningDetails(customPlanningData);
        setExistingPlan(response?.data?.data?.existing_plan);
        setOverlapPlans(response?.data?.data?.overlap_plans);
      } else {
        HandleNotification("error", "bottomRight", "Error generating data", null);
      }
    },
    [createdWith],
  );

  const calculateRemainingQty = (data) => {
    const { order_qty, plan_qty, actual_qty, start_date } = data;
    const planQty = plan_qty || 0;
    const actualQty = actual_qty || 0;
    const current_date = moment();
    const isPlanGreaterCurrentDate = moment(start_date).isAfter(current_date);
    const isCurrentGreaterEqualPlanDate = moment(current_date).isSameOrAfter(start_date);

    const remainingQty =
      order_qty - (isPlanGreaterCurrentDate && planQty) - (isCurrentGreaterEqualPlanDate && actualQty);

    return remainingQty || 0;
  };

  const onFinish = useCallback(
    async (editMode, id) => {
      // Form values
      const formValues = form.getFieldsValue();
      const remaining_qty_form_value = form.getFieldValue("remaining_qty");

      // Show error if totalPlanCurveQty is greater than order_qty_form_value
      if (remaining_qty_form_value < 0) {
        HandleNotification(
          "error",
          "bottomRight",
          "Total Plan Qty is greater than Remaining Qty. Reduce the input value.",
          null,
        );
        return;
      }

      const selectedPoMapIds = isArrayAndHasValue(formValues?.sales_contract_po_id)
        ? formValues.sales_contract_po_id
        : [];
      const savedPoMaps = editMode && isArrayAndHasValue(selectedPlanDetails?.po_maps) ? selectedPlanDetails.po_maps : [];
      const po_maps = [
        ...selectedPoMapIds.map((poMapId) => {
          const saved = savedPoMaps.find((item) => item?.po_map_id === poMapId);
          return { id: saved?.id || 0, po_map_id: poMapId, is_delete: 0 };
        }),
        ...savedPoMaps
          .filter((item) => !selectedPoMapIds.includes(item?.po_map_id))
          .map((item) => ({ id: item.id, po_map_id: item.po_map_id, is_delete: 1 })),
      ];

      const activePlanningDetails = isArrayAndHasValue(planningDetails)
        ? planningDetails.filter((item) => Number(item?.is_delete) !== 1 && Number(item?.replace_status) !== 1)
        : [];

      const query = editMode ? `${PLAN_INITIATE}/${id}` : `${PLAN_INITIATE}`;
      const payload = {
        ob_id: formValues?.ob_id || null,
        learning_curve_id: formValues?.learning_curve_id || null,
        line_id: formValues?.line,
        inquiry_id: formValues?.inquiry_id || formValues?.inquiry_id_when_not_selected || null,
        style_id: formValues?.style_id || null,
        sub_style_id: formValues?.sub_style_id || null,
        buyer: formValues?.buyer?.id,
        buying_office: formValues?.buying_office?.id,
        sales_contract_id: formValues?.sales_contract?.id || null,
        po_maps,
        order_qty: Number(formValues?.origin_order_qty) || 0, // Send origin order qty as order_qty
        req_qty: Number(formValues?.order_qty) || 0, // req_qty is the calculated order qty with cutting percentage
        extra_cutting_percentage: Number(formValues?.extra_cutting_percentage) || 0,
        pcd: formValues?.pcd,
        shipment_date: formValues?.shipment_date ? moment(formValues?.shipment_date).format("YYYY-MM-DD") : null,
        sam: formValues?.smv,
        mp: formValues?.no_of_workers,
        hourly_production: formValues?.per_hour_target,
        order_status: formValues?.order_status_name || null,
        start_date: formValues?.start_date ? moment(formValues?.start_date).format("YYYY-MM-DD") : null,
        end_date: isArrayAndHasValue(activePlanningDetails)
          ? moment(activePlanningDetails[activePlanningDetails.length - 1].date).format("YYYY-MM-DD")
          : formValues?.start_date
            ? moment(formValues.start_date).format("YYYY-MM-DD")
            : null,
        is_apply_learning_curve: formValues?.is_apply_learning_curve ? 1 : 0,
        planned_qty: 0, //Always 0
        planned_mh: 0, //Always 0
        actual_qty: formValues?.actual_qty || null,
        actual_mh: formValues?.actual_mh || null,
        remaining_qty: calculateRemainingQty(formValues),
        factory_id: formValues?.sub_contract_factory_id || null,
        details:
          isArrayAndHasValue(planningDetails) &&
          planningDetails?.map((item) => {
            return {
              id: editMode ? item?.id || 0 : 0,
              is_delete: editMode ? (Number(item?.is_delete) === 1 ? 1 : 0) : 0,
              replace_status: editMode ? (item?.replace_status ?? null) : null,
              day_id: item?.day,
              details_date: item?.date,
              learning_curve_mh: item?.working_hours || item?.learning_curve_mh || 0,
              learning_curve_efficiency: item?.efficiency || item?.learning_curve_efficiency || 0,
              learning_curve_qty: item?.dayQty || item?.learning_curve_qty || 0,
              plan_curve_mh: item?.plan_curve_mh,
              plan_curve_efficiency: item?.plan_curve_efficiency,
              plan_curve_qty: item?.plan_curve_qty,
            };
          }),
        created_with: editMode ? formValues?.created_with : createdWith,
      };

      const response = editMode ? await putData(query, payload) : await postData(query, payload);

      if (response && response?.code === 200) {
        HandleNotification("success", "bottomRight", `Plan ${editMode ? "updated" : "initiated"} successfully`, null);

        if (!editMode) {
          // Reset po field and dropdown options if po have been changed
          if (isArrayAndHasValue(formValues?.sales_contract_po_id)) {
            form.setFieldsValue({
              sales_contract_po_id: [],
            });

            onInquiryAndStyleChange({
              style_id_prop: formValues?.style_id,
              options: options,
            });
          } else {
            form.setFieldsValue({
              planned_qty: 0,
            });
          }

          // if details last item plan hours is less than 10 then not change start_date field but equal to last item plan hours 10 or more then start_date field set next day base on details last item date
          if (isArrayAndHasValue(planningDetails) && planningDetails[planningDetails.length - 1]?.plan_curve_mh >= 10) {
            form.setFieldsValue({
              start_date: moment(planningDetails[planningDetails.length - 1]?.date).add(1, "days"),
            });
          } else {
            form.setFieldsValue({
              start_date: moment(planningDetails[planningDetails.length - 1]?.date),
            });
          }

          // Remove all the tables
          setPlanningDetails(null);
          setExistingPlan(isArrayAndHasValue(response?.data?.existingPlan) ? response?.data?.existingPlan : null);
          setOverlapPlans(null);
        }
      } else {
        HandleNotification("error", "bottomRight", "Error initiating plan", null);
      }
    },
    [form, planningDetails, selectedPlanDetails],
  );

  const onDeletePlan = (day, action) => {
    if (!isArrayAndHasValue(planningDetails)) return;

    const index = planningDetails.findIndex((item) => item.day === day);
    if (index === -1) return;

    if (action === "delete" || action === "replace") {
      const newPlanningDetails = planningDetails.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        if (action === "replace") {
          return {
            ...item,
            is_delete: 1,
            replace_status: 1,
          };
        }

        return {
          ...item,
          is_delete: 1,
          replace_status: 0,
        };
      });
      setPlanningDetails(newPlanningDetails);
      return;
    }

    setPlanningDetails(planningDetails.slice(0, index));
  };

  const onEditPlan = (day) => {
    const newPlanningDetails =
      isArrayAndHasValue(planningDetails) &&
      planningDetails?.map((item) => {
        if (item.day === day) {
          return {
            ...item,
            isEnableField: item?.isEnableField ? false : true,
          };
        }
        return item;
      });
    setChangeStatus(null);
    setPlanningDetails(newPlanningDetails);
  };

  const onChangePlanValue = (day, value, context) => {
    // Set change status
    const status = context === "plan_curve_mh" ? "only_wh" : "only_efficiency";
    if (changeStatus) {
      if (changeStatus === "only_wh" && context === "plan_curve_mh") {
        setChangeStatus("only_wh");
      } else if (changeStatus === "only_efficiency" && context === "plan_curve_efficiency") {
        setChangeStatus("only_efficiency");
      } else {
        setChangeStatus("both");
      }
    } else {
      setChangeStatus(status);
    }

    const newPlanningDetails =
      isArrayAndHasValue(planningDetails) &&
      planningDetails?.map((item) => {
        if (item?.day === day) {
          const SAM = inquiryStyleDetails?.smv;
          const Workers = inquiryStyleDetails?.no_of_workers || form.getFieldValue("no_of_workers");

          let plan_curve_qty_value = null;
          let plan_curve_mh_value = context === "plan_curve_efficiency" ? item?.plan_curve_mh : null;
          let plan_curve_efficiency_value = context === "plan_curve_mh" ? item?.plan_curve_efficiency : null;

          if (context === "plan_curve_mh") {
            const calculatedValue = (value * 60 * Workers * item?.plan_curve_efficiency) / SAM;
            const finalValue = calculatedValue ? Math.ceil(calculatedValue) : 0;
            plan_curve_qty_value = finalValue;
            plan_curve_mh_value = value;
          }
          if (context === "plan_curve_efficiency") {
            const calculatedValue = (item?.plan_curve_mh * 60 * Workers * value) / SAM;
            const finalValue = calculatedValue ? Math.ceil(calculatedValue) : 0;
            plan_curve_qty_value = finalValue;
            plan_curve_efficiency_value = value;
          }

          return {
            ...item,
            edited: true,
            plan_curve_efficiency: plan_curve_efficiency_value,
            plan_curve_mh: plan_curve_mh_value,
            plan_curve_qty: plan_curve_qty_value,
          };
        }
        return item;
      });
    setPlanningDetails(newPlanningDetails);
  };

  const planDataChangeOnLC = (value) => {
    const planningDetailsCopy = isArrayAndHasValue(planningDetails) ? [...planningDetails] : [];
    const maxValueWH = planningDetailsCopy?.reduce((acc, current) => {
      return (acc = acc > current.working_hours ? acc : current.working_hours);
    }, 0);
    const maxValueEfficiency = planningDetailsCopy?.reduce((acc, current) => {
      return (acc = acc > current.efficiency ? acc : current.efficiency);
    }, 0);
    const maxValueDayQty = planningDetailsCopy?.reduce((acc, current) => {
      return (acc = acc > current.dayQty ? acc : current.dayQty);
    }, 0);

    if (value) {
      planningDetailsCopy.forEach((item) => {
        item.plan_curve_mh = item.working_hours;
        item.plan_curve_efficiency = item.efficiency;
        item.plan_curve_qty = item.dayQty;
      });
    } else {
      planningDetailsCopy.forEach((item) => {
        item.plan_curve_mh = maxValueWH;
        item.plan_curve_efficiency = maxValueEfficiency;
        item.plan_curve_qty = maxValueDayQty;
      });
    }

    setPlanningDetails(planningDetailsCopy);
  };

  const regenerateOnline = async (
    data,
    resultArray,
    totalPlanCurveQty,
    daysToDelete,
    isEditMode = false,
    originalPlanTotal = 0,
    removedDetails = [],
  ) => {
    // Get values from form
    const order_qty_form_value = form.getFieldValue("order_qty");
    const actual_qty_form_value = form.getFieldValue("actual_qty");
    const existing_plan_sum_form_value = form.getFieldValue("existing_plan_sum");
    const line = form.getFieldValue("line");
    const smv = form.getFieldValue("smv");
    const no_of_workers = form.getFieldValue("no_of_workers");
    const start_date = form.getFieldValue("start_date");

    let finalRemainingQty;

    if (isEditMode && originalPlanTotal > 0) {
      // In edit mode, regenerate based on original plan total, not full order_qty
      // remaining_qty = original plan total - qty up to edited row
      finalRemainingQty = Math.max(0, originalPlanTotal - totalPlanCurveQty);
    } else {
      // In create mode, use full order_qty calculation
      const remaining_qty =
        Number(order_qty_form_value) -
        ((existing_plan_sum_form_value || 0) + (totalPlanCurveQty || 0) + Number(actual_qty_form_value || 0));

      finalRemainingQty = Math.max(0, remaining_qty);
    }

    // Show error if planned qty is greater than remaining qty
    if (finalRemainingQty < 1) {
      HandleNotification(
        "error",
        "bottomRight",
        "Total Plan Qty is greater than Remaining Qty. Reduce the input value.",
        null,
      );
      return;
    }

    // return;

    const query = `${REGENERATE_PLAN}`;
    const payload = {
      day: data.day,
      plan_start_date: start_date ? moment(start_date).format("YYYY-MM-DD") : null,
      date: data.date,
      plan_curve_mh: data.plan_curve_mh,
      plan_curve_efficiency: data.plan_curve_efficiency,
      plan_curve_qty: data.plan_curve_qty,
      remaining_qty: finalRemainingQty,
      smv: smv,
      no_of_workers: no_of_workers,
      daysToDelete: daysToDelete,
      change_status: changeStatus,
      line_id: line,
    };

    const response = await postData(query, payload);

    if (response && response?.code === 200) {
      const planningXtraDays = isArrayAndHasValue(response?.data?.days) ? response?.data?.days : [];
      const overlapPlans = isArrayAndHasValue(response?.data?.overlap_plans) ? response?.data?.overlap_plans : [];
      const resultArrayCopy = isArrayAndHasValue(resultArray) ? [...resultArray] : null;
      // Turn edited flag to false for current selected plan day
      const foundItem = isArrayAndHasValue(resultArrayCopy) && resultArrayCopy.find((item) => item?.day === data?.day);
      if (foundItem) {
        foundItem["edited"] = false;
      }

      setChangeStatus(null);
      setPlanningDetails(
        [...resultArrayCopy, ...planningXtraDays, ...removedDetails].map((item) => ({
          ...item,
          base_plan_curve_qty: item?.plan_curve_qty,
        })),
      );
      setOverlapPlans(overlapPlans);
    } else {
      HandleNotification("error", "bottomRight", "Error regenerating plan", null);
    }
  };

  const onRegenerate = (record, isEditMode = false) => {
    const planningDetailsCopy = isArrayAndHasValue(planningDetails) ? [...planningDetails] : [];
    const isRemoved = (item) => Number(item?.is_delete) === 1 || Number(item?.replace_status) === 1;
    const removedDetails = planningDetailsCopy.filter(isRemoved);
    const activeDetails = planningDetailsCopy.filter((item) => !isRemoved(item));

    // Original total uses each row's qty from before this edit.
    // The edited row keeps that snapshot in base_plan_curve_qty.
    const originalPlanTotal = activeDetails.reduce((acc, item) => {
      const baseQty = item?.base_plan_curve_qty != null ? item.base_plan_curve_qty : item?.plan_curve_qty;
      return acc + Math.round(parseFloat(baseQty || 0));
    }, 0);

    const foundDayIndex = activeDetails.findIndex((item) => item?.day === record?.day);
    const daysToDelete = foundDayIndex !== -1 ? activeDetails.slice(foundDayIndex + 1) : [];
    // Delete all the items after the found index
    const resultArray = foundDayIndex !== -1 ? activeDetails.slice(0, foundDayIndex + 1) : activeDetails;
    setPlanningDetails([...resultArray, ...removedDetails]);

    // Qty kept from the start through the edited row, using the new qty on that row.
    // Earlier rows stay as they are. The edited row is not part of remaining_qty.
    const totalPlanCurveQty = resultArray?.reduce((acc, current) => {
      return acc + Math.round(parseFloat(current.plan_curve_qty || 0));
    }, 0);

    regenerateOnline(
      record,
      resultArray,
      totalPlanCurveQty,
      daysToDelete,
      isEditMode,
      originalPlanTotal,
      removedDetails,
    );
  };

  const getPlanDetails = useCallback(async (id) => {
    const query = `${PLAN_INITIATE}/${id}/show`;
    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      setSelectedPlanDetails(response?.data?.data);
    } else {
      HandleNotification("error", "bottomRight", "Error getting data", null);
    }
  }, []);

  const onSelectBuyerInquiry = (value, customItem = null) => {
    const foundItem = customItem || options?.inquiryList?.find((item) => item.id === value);
    const styleId = foundItem?.style_info?.id || foundItem?.style_inquiry_map?.style_info?.id;
    const styleObject = foundItem?.style_info || foundItem?.style_inquiry_map?.style_info;

    const subStyleList = isArrayAndHasValue(styleObject?.sub_style) ? styleObject?.sub_style : [];
    setOptions((prev) => ({ ...prev, subStyleList }));

    const isExistsStyle = options?.styleList?.find((item) => item?.id === styleObject?.id);

    if (!isExistsStyle && styleObject) {
      const styleList = isArrayAndHasValue(options?.styleList)
        ? [...options?.styleList, styleObject]
        : [styleObject];
      setOptions((prev) => ({ ...prev, styleList }));
    }

    const inquiryExtraPercent = foundItem?.extra_percent != null ? parseFloat(foundItem.extra_percent) : 0;

    if (styleId) {
      // Set style id and extra_cutting_percentage in form
      form.setFieldsValue({
        style_id: styleId,
        extra_cutting_percentage: inquiryExtraPercent,
      });

      // Change other fields based on selected style
      onSelectStyle(styleId, value);
    } else {
      // Reset style id field
      form.resetFields(["style_id", "sub_style_id"]);

      // Set data to form fields
      form.setFieldsValue({
        buying_office: {
          id: foundItem?.buying_office_info?.id,
          name: foundItem?.buying_office_info?.name,
        },
        buyer: {
          id: foundItem?.buyer_info?.id,
          name: foundItem?.buyer_info?.name,
        },
        description: foundItem?.description,
        subcontract_status: foundItem?.subcontract_status ? 1 : 0,
        sub_contract_factory_id: foundItem?.sub_contract_factory_id || foundItem?.factory_info?.id || null,
        factory_name: foundItem?.factory_info?.name || null,
        extra_cutting_percentage: inquiryExtraPercent,
      });
    }
  };

  const onSelectStyle = (value, inquiry_id) => {
    const foundStyle = options?.styleList?.find((style) => style.id === value);
    const subStyles = foundStyle?.sub_style;

    if (!inquiry_id_watch) {
      setOptions((prev) => ({
        ...prev,
        subStyleList: isArrayAndHasValue(subStyles) ? subStyles : [],
      }));
    }

    const fieldsToSet = {
      inquiry_id: inquiry_id || foundStyle?.inquiry_id || null,
      buying_office: {
        id: foundStyle?.buying_office_info?.id,
        name: foundStyle?.buying_office_info?.name,
      },
      buyer: {
        id: foundStyle?.buyer_info?.id,
        name: foundStyle?.buyer_info?.name,
      },
      sales_contract: {
        id: foundStyle?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        sales_contract_no: foundStyle?.active_sales_contract_map_info?.active_sales_contract_info?.reference_no,
      },
      description: foundStyle?.description,
      sub_style_id: null,
      subcontract_status: foundStyle?.subcontract_status ? 1 : 0,
      sub_contract_factory_id: foundStyle?.sub_contract_factory_id || foundStyle?.factory_info?.id || null,
      factory_name: foundStyle?.factory_info?.name || null,
    };

    if (!inquiry_id) {
      fieldsToSet.extra_cutting_percentage = 0;
    }

    form.setFieldsValue(fieldsToSet);
  };

  const onSelectSubStyle = (value) => {
    const foundSubStyle = options?.subStyleList?.find((item) => item?.id === value);
    const foundStyleId = foundSubStyle?.style_info?.id;
    const isExistsStyle = options?.styleList?.find((item) => item?.id === foundStyleId);

    if (style_id_watch) {
      return;
    }

    if (!isExistsStyle) {
      const styleList = isArrayAndHasValue(options?.styleList)
        ? [...options?.styleList, foundSubStyle?.style_info]
        : [foundSubStyle?.style_info];
      setOptions((prev) => ({ ...prev, styleList }));
    }

    form.resetFields(["style_id"]);

    form.setFieldsValue({
      style_id: foundStyleId,
      buying_office: {
        id: foundSubStyle?.style_info?.buying_office_info?.id,
        name: foundSubStyle?.style_info?.buying_office_info?.name,
      },
      buyer: {
        id: foundSubStyle?.style_info?.buyer_info?.id,
        name: foundSubStyle?.style_info?.buyer_info?.name,
      },
      sales_contract: {
        id: foundSubStyle?.style_info?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        sales_contract_no:
          foundSubStyle?.style_info?.active_sales_contract_map_info?.active_sales_contract_info?.reference_no,
      },
      description: foundSubStyle?.style_info?.description,
      inquiry_id: foundSubStyle?.style_info?.inquiry_id,
      subcontract_status: foundSubStyle?.style_info?.subcontract_status ? 1 : 0,
      sub_contract_factory_id:
        foundSubStyle?.style_info?.sub_contract_factory_id || foundSubStyle?.style_info?.factory_info?.id || null,
      factory_name: foundSubStyle?.style_info?.factory_info?.name || null,
    });
  };

  const onChangeCreatedStatus = (status) => {
    setCreatedWith(status);
  };

  const onDeleteInitiatePlan = async (id) => {
    const query = `/api/productionPlanning/plan_initiate/${id}`;
    const response = await deleteData(query);

    if (response && response?.code === 200) {
      HandleNotification("success", "bottomRight", "Plan deleted successfully", null);
      navigate("/production-planning/dashboard");
    } else {
      HandleNotification("error", "bottomRight", "Error deleting plan", null);
    }
  };

  // Auto-select inquiry, order_qty, shipment_date if provided (e.g. from Planning Request)
  useEffect(() => {
    if (!inquiry_id_param) return;

    const autoSelectFromParams = async () => {
      const numInquiryId = parseInt(inquiry_id_param);
      const buyerInquiryFromState = location.state?.buyer_inquiry;

      if (buyerInquiryFromState) {
        setOptions((prev) => {
          const list = prev?.inquiryList || [];
          return list.some((item) => item.id === numInquiryId)
            ? prev
            : { ...prev, inquiryList: [buyerInquiryFromState, ...list] };
        });
        onSelectBuyerInquiry(numInquiryId, buyerInquiryFromState);
      } else {
        try {
          const res = await getData(BUYER_INQUIRY_LIST_ENDPOINT, false, {
            inquiry_no: inquiry_no_param || undefined,
            per_page: 10,
          });
          if (res && res?.data?.code === 200) {
            const list = res?.data?.data?.data || [];
            const matched = list.find((item) => item.id === numInquiryId) || list[0];
            if (matched) {
              setOptions((prev) => {
                const existing = prev?.inquiryList || [];
                return existing.some((item) => item.id === matched.id)
                  ? prev
                  : { ...prev, inquiryList: [matched, ...existing] };
              });
              onSelectBuyerInquiry(matched.id, matched);
            }
          }
        } catch (e) {
          console.error("Error auto-selecting inquiry:", e);
        }
      }

      await onInquiryAndStyleChange({ inquiry_id_prop: numInquiryId });
      onChangeCreatedStatus("with_inquiry");

      const fieldsToSet = {
        inquiry_id: numInquiryId,
        sales_contract_po_id: [],
      };

      if (order_qty_param !== undefined && order_qty_param !== null && order_qty_param !== "") {
        const numQty = Number(order_qty_param);
        fieldsToSet.order_qty = numQty;
        fieldsToSet.origin_order_qty = numQty;
        fieldsToSet.origin_order_qty_display = numQty;
      }

      if (shipment_date_param && moment(shipment_date_param).isValid()) {
        fieldsToSet.shipment_date = moment(shipment_date_param);
      }

      form.setFieldsValue(fieldsToSet);
    };

    autoSelectFromParams();
  }, [inquiry_id_param, onInquiryAndStyleChange, form]);

  return (
    <ProductionPlanningContext.Provider
      value={{
        form,
        options,
        setOptions,
        onGenerate,
        onFinish,
        planningDetails,
        setPlanningDetails,
        existingPlan,
        setExistingPlan,
        overlapPlans,
        setOverlapPlans,
        planningOverlapping,
        setPlanningOverlapping,
        onInquiryAndStyleChange,
        inquiryStyleDetails,
        onDeletePlan,
        onEditPlan,
        onChangePlanValue,
        planDataChangeOnLC,
        onRegenerate,
        getPlanDetails,
        selectedPlanDetails,
        getInquiryList,
        getStyleList,
        getSubStyleList,
        onSelectBuyerInquiry,
        onSelectStyle,
        onSelectSubStyle,
        onChangeCreatedStatus,
        shipmentDateSetter,
        onDeleteInitiatePlan,
        isGenerateBtnEnable,
        startDateOrLineChange,
        onPcdRequestChange,
        resetPcdRequests,
      }}
    >
      {children}
    </ProductionPlanningContext.Provider>
  );
};

export default ProductionPlanningContextProvider;
